class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.mainCurrentIndex = 0;
    this.thumbSlideClickHandlers = [];
    this.dotClickHandlers = [];
    this.updateMainCurrentIndex = this.updateMainCurrentIndex.bind(this);
    this.thumbPrevClick = this.thumbPrevClick.bind(this);
    this.thumbNextClick = this.thumbNextClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleMainScroll = this.debounce(this.updateMainCurrentIndex, 80);
  }

  static get observedAttributes() {
    return ["data-active-variant-handle"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-active-variant-handle") {
      this.onVariantChange(newValue);
      this.checkThumbnailArrows();
    }
  }

  connectedCallback() {
    this.autoplay = this.dataset.autoplay === 'true';
    this.muteOnAutoplay = this.dataset.muteOnAutoplay === 'true';
    this.videoEndedHandlers = [];
    this.cacheDom();
    this.initializeGallery();
    this.bindVideoLoopFallbacks();
    this.checkThumbnailArrows();
    window.addEventListener("resize", this.handleResize);
  }
  disconnectedCallback() {
    this.removeEventListeners();
    this.unbindVideoLoopFallbacks();
    window.removeEventListener("resize", this.handleResize);
  }

  cacheDom() {
    this.mainMediaGallery = this.querySelector('.main-mediaGallery');
    this.mainSlides = Array.from(this.querySelectorAll('.main-slide'));
    this.thumbMediaGallery = this.querySelector('.thumbnail-mediaGallery');
    this.thumbSlides = Array.from(this.querySelectorAll('.thumbnail-slide'));
    this.thumbPrevBtn = this.querySelector('.thumb-prev-btn');
    this.thumbNextBtn = this.querySelector('.thumb-next-btn');
    this.thumbTrack = this.querySelector('.thumbnail-mediaGallery-track');
    this.galleryContainer = this;
    this.navTrack = this.querySelector('.mediaGallery-nav-track');
  }

  handleResize() {
    this.checkThumbnailArrows();
    // Recalculate after resize in case of mobile/desktop switch
    this.updateMainCurrentIndex();
    this.adjustThumbnailScroll();
  }

  checkThumbnailArrows() {
    if (!this.galleryContainer || !this.thumbTrack) return;
    requestAnimationFrame(() => {
      if (this.thumbTrack.scrollWidth <= this.thumbTrack.parentElement.clientWidth + 1) {
        this.galleryContainer.classList.add("no-thumbnail-arrows");
      } else {
        this.galleryContainer.classList.remove("no-thumbnail-arrows");
      }
    });
  }

  onVariantChange(newVariantHandle) {
    this.updateVisibleSlides();
    this.initializeGallery();
    this.checkThumbnailArrows();
  }

  initializeGallery() {
    this.updateVisibleSlides();
    this.removeEventListeners();

    // Main scroll listener (to auto-update index)
    if (this.mainMediaGallery) {
      this.mainMediaGallery.addEventListener('scroll', this.handleMainScroll);
    }

    // Nav dots
    if (this.navDots) {
      this.navDots.forEach((dot, dotIndex) => {
        const handler = () => {
          this.scrollToMainSlide(dotIndex, true);
        };
        dot.addEventListener("click", handler);
        this.dotClickHandlers.push({ dot, handler });
      });
    }

    // Arrow navigation
    if (this.thumbPrevBtn) this.thumbPrevBtn.addEventListener("click", this.thumbPrevClick);
    if (this.thumbNextBtn) this.thumbNextBtn.addEventListener("click", this.thumbNextClick);

    // Thumbnail click
    if (this.visibleThumbSlides) {
      this.visibleThumbSlides.forEach((thumbSlide, visibleIndex) => {
        const handler = () => {
          this.scrollToMainSlide(visibleIndex, true);
        };
        thumbSlide.addEventListener("click", handler);
        this.thumbSlideClickHandlers.push({ thumbSlide, handler });
      });
    }

    // On init, go to first slide
    this.scrollToMainSlide(this.mainCurrentIndex, false); // No animation for init
    this.updateActiveThumbnail();
    this.updateActiveNavDot();
    this.checkThumbnailArrows();
  }

  removeEventListeners() {
    if (this.mainMediaGallery) {
      this.mainMediaGallery.removeEventListener('scroll', this.handleMainScroll);
    }
    if (this.thumbPrevBtn) this.thumbPrevBtn.removeEventListener("click", this.thumbPrevClick);
    if (this.thumbNextBtn) this.thumbNextBtn.removeEventListener("click", this.thumbNextClick);

    this.thumbSlideClickHandlers.forEach(({ thumbSlide, handler }) => thumbSlide.removeEventListener("click", handler));
    this.thumbSlideClickHandlers = [];

    this.dotClickHandlers.forEach(({ dot, handler }) => dot.removeEventListener("click", handler));
    this.dotClickHandlers = [];
  }

  // Always updates the list of visible slides and (re)builds nav dots
  updateVisibleSlides() {
    this.visibleMainSlides = this.getVisibleSlides(this.mainSlides);
    this.visibleThumbSlides = this.getVisibleSlides(this.thumbSlides);
    this.renderNavDots();
  }

  renderNavDots() {
    if (!this.navTrack || !this.visibleMainSlides) return;
    this.navTrack.innerHTML = "";
    this.visibleMainSlides.forEach((_, idx) => {
      const dot = document.createElement("button");
      dot.className = "nav-dot" + (idx === this.mainCurrentIndex ? " active" : "");
      dot.setAttribute("aria-label", `Slide ${idx + 1}`);
      dot.type = "button";
      this.navTrack.appendChild(dot);
    });
    this.navDots = Array.from(this.navTrack.querySelectorAll(".nav-dot"));
  }

  getVisibleSlides(slides) {
    return slides?.filter(slide => slide.offsetParent !== null);
  }

  scrollToMainSlide(visibleIndex, smooth = true) {
    if (!this.visibleMainSlides || !this.mainMediaGallery) return;
    const targetSlide = this.visibleMainSlides[visibleIndex];

    if (targetSlide) {
      const center = targetSlide.offsetLeft - this.mainMediaGallery.offsetLeft;
      this.mainMediaGallery.scrollTo({
        left: center,
        behavior: smooth ? "smooth" : "auto"
      });
      // Set index now (if not smooth), else will get updated on scroll event
      if (!smooth) {
        this.mainCurrentIndex = visibleIndex;
        this.updateActiveThumbnail();
        this.updateActiveNavDot();
        this.adjustThumbnailScroll();
      }
    }
  }

  // The core: Always update mainCurrentIndex based on which slide is most visible in viewport
  updateMainCurrentIndex() {
    if (!this.mainMediaGallery || !this.visibleMainSlides?.length) return;
    const galleryRect = this.mainMediaGallery.getBoundingClientRect();
    let newIndex = 0;
    let maxIntersection = 0;

    // Find slide occupying largest width in main gallery viewport
    this.visibleMainSlides.forEach((slide, idx) => {
      const slideRect = slide.getBoundingClientRect();
      // Overlap between slideRect and galleryRect in X axis
      const left = Math.max(slideRect.left, galleryRect.left);
      const right = Math.min(slideRect.right, galleryRect.right);
      const intersection = Math.max(0, right - left);
      if (intersection > maxIntersection) {
        maxIntersection = intersection;
        newIndex = idx;
      }
    });

    if (this.mainCurrentIndex !== newIndex) {
      const oldIndex = this.mainCurrentIndex;
      this.mainCurrentIndex = newIndex;
      this.updateActiveThumbnail();
      this.updateActiveNavDot();
      this.adjustThumbnailScroll();
      this.handleAutoplay(oldIndex, newIndex);
    }
  }

  handleAutoplay(oldIndex, newIndex) {
    if (!this.autoplay || !this.visibleMainSlides) return;

    const oldSlide = this.visibleMainSlides[oldIndex];
    if (oldSlide) {
      oldSlide.querySelector('video')?.pause();
      oldSlide.querySelector('iframe.js-youtube')?.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}', '*'
      );
      oldSlide.querySelector('iframe.js-vimeo')?.contentWindow?.postMessage(
        '{"method":"pause"}', '*'
      );
    }

    const newSlide = this.visibleMainSlides[newIndex];
    if (newSlide) {
      const video = newSlide.querySelector('video');
      if (video) {
        if (this.muteOnAutoplay) video.muted = true;
        video.play().catch(() => {});
      }
      const yt = newSlide.querySelector('iframe.js-youtube');
      if (yt) {
        if (this.muteOnAutoplay) yt.contentWindow?.postMessage('{"event":"command","func":"mute","args":""}', '*');
        yt.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      }
      const vimeo = newSlide.querySelector('iframe.js-vimeo');
      if (vimeo) {
        if (this.muteOnAutoplay) vimeo.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*');
        vimeo.contentWindow?.postMessage('{"method":"play"}', '*');
      }
    }
  }

  thumbPrevClick() {
    // Find current main index using visible slides and move to previous
    if (!this.visibleMainSlides?.length) return;
    let prev = this.mainCurrentIndex > 0 ? this.mainCurrentIndex - 1 : 0;
    this.scrollToMainSlide(prev, true); // will call updateMainCurrentIndex
  }

  thumbNextClick() {
    if (!this.visibleMainSlides?.length) return;
    if (this.mainCurrentIndex >= this.visibleMainSlides.length - 1) {
      // We are at (or past) the last slide, so reset to first slide
      this.scrollToMainSlide(0, true);
    } else {
      // Normal next behavior
      this.scrollToMainSlide(this.mainCurrentIndex + 1, true);
    }
  }

  adjustThumbnailScroll() {
    const index = this.mainCurrentIndex;
    const slides = this.visibleThumbSlides || this.thumbSlides;
    const target = slides[index];
    if (!target || !this.thumbMediaGallery) return;

    const slideOffset = target.offsetLeft;
    const slideWidth = target.offsetWidth;
    const scrollPos = this.thumbMediaGallery.scrollLeft;
    const containerWidth = this.thumbMediaGallery.clientWidth;

    // If the left edge of the target is outside the visible area, scroll left
    if (slideOffset < scrollPos) {
      this.thumbMediaGallery.scrollTo({
        left: slideOffset,
        behavior: "smooth",
      });
      return;
    }

    // If the right edge of the target is outside the visible area, scroll right
    if (slideOffset + slideWidth > scrollPos + containerWidth) {
      this.thumbMediaGallery.scrollTo({
        left: slideOffset + slideWidth - containerWidth,
        behavior: "smooth",
      });
      return;
    }
    // Else, already in view—do nothing
  }

  updateActiveThumbnail() {
    if (!this.visibleThumbSlides) return;
    this.visibleThumbSlides.forEach((thumbSlide, idx) => {
      thumbSlide.classList.toggle("active", idx === this.mainCurrentIndex);
    });
  }

  updateActiveNavDot() {
    if (!this.navDots) return;
    this.navDots.forEach((dot, idx) => {
      dot.classList.toggle("active", idx === this.mainCurrentIndex);
    });
  }

  // Reinforce native `loop` attribute against browser seek-to-end quirk
  bindVideoLoopFallbacks() {
    this.querySelectorAll('.main-slide video').forEach(video => {
      const handler = () => {
        if (!video.loop) return;
        video.currentTime = 0;
        video.play().catch(() => {});
      };
      video.addEventListener('ended', handler);
      this.videoEndedHandlers.push({ video, handler });
    });
  }

  unbindVideoLoopFallbacks() {
    this.videoEndedHandlers.forEach(({ video, handler }) => {
      video.removeEventListener('ended', handler);
    });
    this.videoEndedHandlers = [];
  }

  // Debounce utility
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Register component
customElements.define("product-media-gallery", ProductMediaGallery);